import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import axios from "axios";

const parseHtmlOptions = (htmlString) => {
  if (!htmlString) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<select>${htmlString}</select>`,
    "text/html"
  );
  const opts = doc.querySelectorAll("option");
  const result = [];
  opts.forEach((opt) => {
    const label = opt.textContent.trim();
    const value = opt.getAttribute("value") || "";
    if (
      label &&
      !label.toLowerCase().includes("--select--") &&
      !label.toLowerCase().includes("----all----") &&
      value !== "" &&
      value !== "0"
    ) {
      result.push({ value, label });
    }
  });
  return result;
};

const API_BASE = process.env.REACT_APP_URL_HEADER;

const GenEntitlementTable = ({
  generatedEntitlements = new Set(),
  policySchemes = [],
  policyCommodities = []
}) => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchDistricts = async () => {
      setLoading(true);
      setError(null);

      try {
        const districtsRes = await axios.get(`${API_BASE}/Common/getDistrictsAll`);
        if (cancelled) return;
        setDistricts(parseHtmlOptions(districtsRes.data));
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching districts table data:", err);
          setError("Failed to load table data. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDistricts();

    return () => {
      cancelled = true;
    };
  }, []);

  const totalDataColumns = policySchemes.length + policyCommodities.length;
  const totalColumns = totalDataColumns + 2;

  const renderStatusCell = (districtValue, itemValue, type) => {
    const compositeKey = `${districtValue}|${type}|${itemValue}`;

    if (generatedEntitlements.has(compositeKey)) {
      return <FaCheckCircle color="green" size={18} />;
    }
    return <FaTimesCircle color="red" size={18} />;
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "30px" }}>
        <p>Loading Entitlement Status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "30px", color: "red" }}>
        <p>{error}</p>
      </div>
    );
  }

  if (policySchemes.length === 0 && policyCommodities.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "30px", color: "#666" }}>
        <p>No schemes or commodities found.</p>
      </div>
    );
  }

  return (
    <table
      align="center"
      width="70%"
      border="1"
      cellPadding="5"
      style={{ marginTop: "30px" }}
    >
      <thead>
        <tr className="tableheader">
          <th colSpan={totalColumns}>Generate Entitlement Status</th>
        </tr>
        <tr className="tableheader">
          <th rowSpan="2">Sl.No</th>
          <th rowSpan="2">District</th>
          {policySchemes.length > 0 && <th colSpan={policySchemes.length}>Schemes</th>}
          {policyCommodities.length > 0 && (
            <th colSpan={policyCommodities.length}>Commodities</th>
          )}
        </tr>

        <tr className="tableheader">
          {policySchemes.map((scheme) => (
            <th key={`scheme-${scheme.value}`}>{scheme.label}</th>
          ))}
          {policyCommodities.map((commodity) => (
            <th key={`comm-${commodity.value}`}>{commodity.label}</th>
          ))}
        </tr>
      </thead>

      <tbody>
        {districts.length > 0 ? (
          districts.map((district, index) => (
            <tr key={`dist-${district.value}`}>
              <td className="text-center">{index + 1}</td>
              <td>{district.label}</td>
              {policySchemes.map((scheme) => (
                <td
                  key={`dist-${district.value}-scheme-${scheme.value}`}
                  className="text-center"
                >
                  {renderStatusCell(district.value, scheme.value, "scheme")}
                </td>
              ))}
              {policyCommodities.map((commodity) => (
                <td
                  key={`dist-${district.value}-comm-${commodity.value}`}
                  className="text-center"
                >
                  {renderStatusCell(district.value, commodity.value, "comm")}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={totalColumns} className="text-center">
              No districts found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default GenEntitlementTable;